import '../modules/mode-shared-services';

import { ReportFilterField } from '@mode/filters/util';
import { ReportResource } from '@mode/legacy/data-access-model-js';
import { ofType } from '@mode/legacy/util-domain';
import {
  ReportFilterFacade,
  fromReportFilterSelectors,
  reportFilterActions,
  reportFilterHelpers,
} from '@mode/report-viewer/domain';
import { RMap, ReportTypes } from '@mode/shared/contract-common';
import * as angular from 'angular';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Actions } from '~/actions';
import { AppStore } from '~/types/main.types';
import { ReportFilterActionType } from '../actions/report-filter-actions';
import { ReportRunActionType } from '../actions/report-run.actions';
import { SelectedReportSelectors } from '../selectors/selected-report.selectors';
import { ReportModelActionType } from './../actions/report-model-actions';
import {
  currentAccountUsernameSelector,
  currentReportTokenSelector,
  reportFilterControlsSelector,
  reportFilterResourcesByKeySelector,
  reportResourcesByKeySelector,
  validReportModelsSelector,
} from './../reducers/selectors';
import DatasetService from './dataset-service';
import { ReportDatasetService } from './report-dataset-service';

export class ReportFilterService {
  static $inject = [
    '$q',
    'RxStoreService',
    'actions$',
    'ReportDatasetService',
    'DatasetService',
    'ReportFilterModel',
    'ToastService',
    'ReportFilterFacade',
  ];

  private _currentReportToken: string;
  private _currentAccountUsername: string;

  constructor(
    private $q: angular.IQService,
    private _storeService: AppStore,
    private actions$: Observable<Actions>,
    private reportDatasetService: ReportDatasetService,
    datasetService: DatasetService,
    private ReportFilterModel: any,
    private AlertService: any,
    private filterFacade: ReportFilterFacade
  ) {
    this.store.select(currentReportTokenSelector).subscribe((currentReportToken) => {
      this._currentReportToken = currentReportToken;
    });

    this.store.select(currentAccountUsernameSelector).subscribe((currentAccountUsername) => {
      this._currentAccountUsername = currentAccountUsername;
    });

    // Effects!
    this.actions$
      .pipe(
        ofType(
          ReportRunActionType.LoadQueryResultFailure,
          ReportRunActionType.LoadQueryResultSuccess,
          ReportRunActionType.ReportRoutesNoScriptOrLegacy
        )
      )
      .pipe(
        withLatestFrom(
          this.store.select(SelectedReportSelectors.getChartModels),
          this.store.select(fromReportFilterSelectors.selectFilterControls)
        ),
        switchMap(([action, models, filterControls]) => {
          const model = models.getRecord(action.queryRun.query_token);
          return reportDatasetService.loadFilteredDataset(model, filterControls).then(
            (dataset) =>
              this.store.dispatch({ type: ReportFilterActionType.FilterReportDatasetSuccess, payload: dataset }),
            (err) => this.store.dispatch({ type: ReportFilterActionType.FilterReportDatasetFailure, error: err })
          );
        })
      )
      .subscribe();

    /**
     * Query Effects (Heavy Lifting)
     * Calling into the engine to do data stuff
     */

    // TODO does this need to exist? why do you live?!
    this.actions$
      .pipe(
        ofType(reportFilterActions.applyReportFilters),
        withLatestFrom(
          this.store.select(validReportModelsSelector),
          this.store.select(fromReportFilterSelectors.selectFilterControls)
        ),
        switchMap(([, models, filterControls]) => {
          return this.reportDatasetService.loadFilteredDatasets(models, filterControls).then(
            (res) => this.store.dispatch({ type: ReportFilterActionType.FilterReportDatasetsSuccess, payload: res }),
            (err) => this.store.dispatch({ type: ReportFilterActionType.FilterReportDatasetsFailure, error: err })
          );
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(ReportFilterActionType.FilterReportDatasets),
        withLatestFrom(
          this.store.select(validReportModelsSelector),
          this.store.select(fromReportFilterSelectors.selectFilterControls)
        ),
        switchMap(([, models, filterControls]) =>
          this.reportDatasetService.loadFilteredDatasets(models, filterControls).then(
            (res) => this.store.dispatch({ type: ReportFilterActionType.FilterReportDatasetsSuccess, payload: res }),
            (err) => this.store.dispatch({ type: ReportFilterActionType.FilterReportDatasetsFailure, error: err })
          )
        )
      )
      .subscribe();

    /**
     * Basic Persistence
     */

    this.actions$
      .pipe(
        ofType(reportFilterActions.createReportFilter),
        switchMap((action) =>
          this.createReportFilterModel(action.accountUsername, action.reportToken, action.filterField)
            .then((res) =>
              reportFilterActions.createReportFilterSuccess({
                resource: res,
              })
            )
            .catch((err) => reportFilterActions.createReportFilterFailure({ error: err }))
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        ofType(reportFilterActions.destroyReportFilter),
        withLatestFrom(this.store.select(reportFilterResourcesByKeySelector)),
        switchMap(([action, reportFilterResources]) =>
          this.destroyReportFilterModel(reportFilterResources.get(action.token))
            .then(() => reportFilterActions.destroyReportFilterSuccess({ token: action.token }))
            .catch((err) => reportFilterActions.destroyReportFilterFailure({ error: err, token: action.token }))
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        ofType(reportFilterActions.updateReportFilter),
        withLatestFrom(this.store.select(reportFilterResourcesByKeySelector)),
        switchMap(([action, reportFilterResources]) =>
          this.updateReportFilterModel(
            action.filter,
            reportFilterResources.get(action.filter.token),
            action.defaultSelection
          )
            .then((resource) => reportFilterActions.updateReportFilterSuccess({ resource }))
            .catch((err) => reportFilterActions.updateReportFilterFailure({ error: err }))
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        ofType(reportFilterActions.updateReportFilterType),
        withLatestFrom(
          this.store.select(fromReportFilterSelectors.selectFilterDefinitions),
          this.store.select(reportFilterResourcesByKeySelector)
        ),
        switchMap(([action, filters, reportFilterResources]) =>
          this.updateReportFilterModel(
            {
              ...filters[action.token],
              filterType: action.filterType,
              varType: reportFilterHelpers.getVarTypeForFilterType(action.filterType),
              controlType: reportFilterHelpers.getControlForFilterType(action.filterType, action.controlType),
              showRelevantValuesOnly:
                action.filterType !== ReportTypes.ReportFilterType.Range
                  ? filters[action.token].showRelevantValuesOnly
                  : false,
              hideSelectAll: false,
            },
            reportFilterResources.get(action.token),
            action.defaultSelection,
            action.defaultSelection != null
          )
            .then((resource) =>
              reportFilterActions.updateReportFilterTypeSuccess({
                resource,
                selection: action.defaultSelection,
                applyFilters: action.applyFilters,
              })
            )
            .catch((err) => reportFilterActions.updateReportFilterTypeFailure({ error: err }))
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        ofType(reportFilterActions.hideSelectAll),
        withLatestFrom(
          this.store.select(fromReportFilterSelectors.selectFilterDefinitions),
          this.store.select(reportFilterResourcesByKeySelector)
        ),
        switchMap(([action, filters, reportFilterResources]) =>
          this.updateReportFilterModel(
            {
              ...filters[action.token],
              hideSelectAll: action.hide,
            },
            reportFilterResources.get(action.token),
            action.selection,
            action.persist
          )
            .then((resource) =>
              reportFilterActions.hideSelectAllSuccess({
                resource,
                selection: action.selection,
                applyFilters: action.persist,
              })
            )
            .catch((err) => reportFilterActions.hideSelectAllFailure({ error: err, token: action.token }))
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        ofType(reportFilterActions.hideSelectAllSuccess, reportFilterActions.updateReportFilterTypeSuccess),
        filter((action) => action.applyFilters),
        withLatestFrom(this.store.select(fromReportFilterSelectors.selectFilterSelections)),
        map(([action, selections]) =>
          reportFilterActions.applyReportFilters({
            selected: {
              ...selections,
              [action.resource.token]: action.selection,
            },
          })
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        ofType(reportFilterActions.showRelevantValues),
        withLatestFrom(
          this.store.select(fromReportFilterSelectors.selectFilterDefinitions),
          this.store.select(reportFilterResourcesByKeySelector)
        ),
        switchMap(([action, filters, reportFilterResources]) =>
          this.updateReportFilterModel(
            {
              ...filters[action.token],
              showRelevantValuesOnly: action.showRelevantValues,
            },
            reportFilterResources.get(action.token)
          )
            .then((resource) =>
              reportFilterActions.showRelevantValuesSuccess({
                resource,
              })
            )
            .catch((err) => reportFilterActions.showRelevantValuesFailure({ error: err, token: action.token }))
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        ofType(reportFilterActions.updateReportFilterDefaults),
        withLatestFrom(this.store.select(reportResourcesByKeySelector)),
        switchMap(([action, reportResources]) =>
          this.updateReportFilterDefaultsModel(action.defaults, reportResources.get(this.currentReportToken))
            .then((res) => reportFilterActions.updateReportFilterDefaultsSuccess({ resources: res }))
            .catch((err) => reportFilterActions.updateReportFilterDefaultsFailure({ error: err }))
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    this.actions$
      .pipe(
        ofType(reportFilterActions.updateReportFilterPosition),
        withLatestFrom(this.store.select(reportFilterResourcesByKeySelector)),
        switchMap(([action, reportFilterResources]) =>
          this.updateReportFilterPositionModel(reportFilterResources.get(action.source), action.destination)
            .then((res) => reportFilterActions.updateReportFilterPositionSuccess({ resources: res }))
            .catch((err) => reportFilterActions.updateReportFilterPositionFailure({ error: err }))
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    /**
     * Persistence `Callback` Handlers
     * TODO Move these to reducers because this pattern is bad
     * Some of these patterns are important though like sequencing server calls
     * We'll have to discuss how to work with each of these patterns
     */

    this.actions$
      .pipe(
        ofType(reportFilterActions.destroyReportFilterSuccess),
        withLatestFrom(this.store.select(validReportModelsSelector), this.store.select(reportFilterControlsSelector))
      )
      .subscribe(([, models, filters]) => {
        this.store.dispatch({
          type: ReportFilterActionType.FilterReportDatasets,
          payload: {
            models,
            filters,
          },
        });
      });

    this.actions$
      .pipe(
        ofType(reportFilterActions.updateReportFilterTypeSuccess),
        withLatestFrom(
          this.store.select(validReportModelsSelector),
          this.store.select(fromReportFilterSelectors.selectFilterControls)
        ),
        switchMap(([, reportModels, filterControls]) =>
          // TODO: This is a valid use case of two effects that must happen sequentially
          // namely that we must wait for a server response to updateing a filter type
          // before we dispatch a server call to load datasets with that new filter info
          this.reportDatasetService.loadFilteredDatasets(reportModels, filterControls).then(
            (res) => this.store.dispatch({ type: ReportFilterActionType.FilterReportDatasetsSuccess, payload: res }),
            (err) => this.store.dispatch({ type: ReportFilterActionType.FilterReportDatasetsFailure, error: err })
          )
        )
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(reportFilterActions.updateReportFilterDefaultsSuccess),
        tap(() => {
          this.AlertService.success('Defaults Set');
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(reportFilterActions.updateReportFilterDefaultsSuccess),
        withLatestFrom(this.store.select(validReportModelsSelector), this.store.select(reportFilterControlsSelector))
      )
      .subscribe(([, models, filters]) => {
        this.store.dispatch({
          type: ReportFilterActionType.FilterReportDatasets,
          payload: {
            models,
            filters,
          },
        });
      });

    this.actions$
      .pipe(
        ofType(reportFilterActions.updateReportFilterDefaultsFailure),
        tap(() => {
          this.AlertService.error('Error saving defaults');
        })
      )
      .subscribe();
  }

  private get store(): AppStore {
    return this._storeService;
  }

  private get currentReportToken(): string {
    return this._currentReportToken;
  }

  private get currentAccountUsername(): string {
    return this._currentAccountUsername;
  }

  updateModels(): void {
    this.store.dispatch({
      type: ReportModelActionType.AddReportModel,
    });
  }

  createReportFilter(filterField: ReportFilterField): void {
    this.filterFacade.createReportFilter(this.currentAccountUsername, this.currentReportToken, filterField);
  }

  private createReportFilterModel(
    accountUsername: string,
    reportToken: string,
    filterField: ReportTypes.ReportFilterField
  ): angular.IPromise<ReportTypes.ReportFilterResource> {
    const deferred = this.$q.defer<ReportTypes.ReportFilterResource>();

    this.ReportFilterModel.create({
      report: reportToken,
      account: accountUsername,
      params: {
        'report_filter.name': filterField.control.name,
        'report_filter.formula': filterField.control.formula,
        'report_filter.data_type': filterField.control.dataType,
        'report_filter.formula_type': filterField.control.formulaType,
        'report_filter.variable_type': filterField.control.varType,
        'report_filter.filter_type': filterField.control.filterType,
        'report_filter.control_type': filterField.control.controlType,
        'report_filter.hide_select_all': filterField.control.hideSelectAll,
        'report_filter.show_relevant_values_only': filterField.control.showRelevantValuesOnly,
      },
    })
      .then((res) => deferred.resolve(res._resource))
      .catch((err) => deferred.reject(err));

    return deferred.promise;
  }

  private destroyReportFilterModel(
    filterResource: ReportTypes.ReportFilterResource
  ): angular.IPromise<ReportTypes.ReportFilterResource> {
    const deferred = this.$q.defer<ReportTypes.ReportFilterResource>();

    new this.ReportFilterModel.model(filterResource)
      .destroy()
      .then((res) => deferred.resolve(filterResource))
      .catch((err) => deferred.reject(err));

    return deferred.promise;
  }

  private updateReportFilterModel(
    control: ReportTypes.FilterDefinition,
    filterResource: ReportTypes.ReportFilterResource,
    selection?: ReportTypes.FilterSelection,
    persistSelection = false
  ): angular.IPromise<ReportTypes.ReportFilterResource> {
    const deferred = this.$q.defer<ReportTypes.ReportFilterResource>();
    let params = {
      'report_filter.name': control.name,
      'report_filter.formula': control.formula,
      'report_filter.data_type': control.dataType,
      'report_filter.formula_type': control.formulaType,
      'report_filter.variable_type': control.varType,
      'report_filter.filter_type': control.filterType,
      'report_filter.control_type': control.controlType,
      'report_filter.hide_select_all': control.hideSelectAll,
      'report_filter.show_relevant_values_only': control.showRelevantValuesOnly,
    };

    if (selection && persistSelection) {
      params['report_filter.options'] = JSON.stringify({ default: selection });
    }

    this.ReportFilterModel.update(new this.ReportFilterModel.model(filterResource), params)
      .then((res) => deferred.resolve(res._resource))
      .catch((err) => deferred.reject(err));

    return deferred.promise;
  }

  private updateReportFilterDefaultsModel(
    defaults: RMap<ReportTypes.FilterSelection>,
    reportResource: Readonly<ReportResource>
  ): angular.IPromise<ReportTypes.ReportFilterResource[]> {
    const deferred = this.$q.defer<ReportTypes.ReportFilterResource[]>();

    reportResource
      .forms('batch_edit_filters')
      .$submit({
        'report.filters[]': Object.keys(defaults).map((token) => ({
          token: token,
          options: {
            default: defaults[token],
          },
        })),
      })
      .then((filterResources) => deferred.resolve(filterResources._embedded.report_filters))
      .catch((err) => deferred.reject(err));

    return deferred.promise;
  }

  private updateReportFilterPositionModel(
    filterResource: ReportTypes.ReportFilterResource,
    nextFilterControlToken: string
  ): angular.IPromise<ReportTypes.ReportFilterResource[]> {
    const deferred = this.$q.defer<ReportTypes.ReportFilterResource[]>();

    filterResource
      .forms('edit_position')
      .$submit({
        'report_filter.next_filter_token': nextFilterControlToken,
      })
      .then((filterResources) => deferred.resolve(filterResources._embedded.report_filters))
      .catch((err) => deferred.reject(err));

    return deferred.promise;
  }
}
